/**
 * A radio group whose options are cards, for a choice that changes the screen.
 *
 * Two of these were tabs, which is wrong twice over: a tablist promises panels
 * that stay put while you move between them, and arrow keys that move without
 * choosing. Picking how to create a programme is a choice, and it belongs to
 * the form it changes.
 *
 * @module components/ui/Form/RadioCard
 */

import React, { type ReactNode, useId } from "react";

import { Icon } from "@/components/ui/Icon";

import { useLockedOptions } from "../EditLock";

export interface RadioCardOption<Value extends string> {
  value: Value;
  /** The choice, in a few words. */
  label: string;
  /** What picking it does. One line. */
  description?: ReactNode;
  icon?: string;
  /** Refused but still reachable, as `aria-disabled` plus a change guard. */
  disabled?: boolean;
  /** Why it is refused: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  "data-testid"?: string;
}

export interface RadioCardGroupProps<Value extends string> {
  /** Names the choice for a screen reader, e.g. "Programme creation method". */
  legend: string;
  options: readonly RadioCardOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  /** Stacks below `sm` either way; two only where the labels are short. */
  columns?: 1 | 2;
  /** Draws the legend above the cards, for a group that has no heading of its own. */
  showLegend?: boolean;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <RadioCardGroup legend="Programme creation method" value={mode} onChange={setMode}
 *   options={[{ value: "scratch", label: "Start from scratch", icon: "file-plus" }]} />
 * ```
 */
export function RadioCardGroup<Value extends string>({
  legend,
  options: ownOptions,
  value,
  onChange,
  columns = 2,
  showLegend = false,
  "data-testid": testId,
}: RadioCardGroupProps<Value>): React.ReactElement {
  const options = useLockedOptions(ownOptions);
  const name = useId();

  return (
    <fieldset data-testid={testId}>
      <legend className={showLegend ? "mb-2 text-sm font-semibold" : "sr-only"}>{legend}</legend>
      <div className={`grid gap-3 ${columns === 2 ? "sm:grid-cols-2" : ""}`.trim()}>
        {options.map((option, index) => {
          const selected = option.value === value;
          const reason = option.disabled ? option.disabledReason : undefined;
          const reasonId = `${name}-reason-${index}`;
          return (
            // The whole card is the label, so the entire target selects the
            // radio inside it and the browser handles the keyboard for us. The
            // reason sits beside it, not in it, or it joins the name.
            <React.Fragment key={option.value}>
              <label
                className={[
                  "flex cursor-pointer items-start gap-3 rounded-box border p-4",
                  selected
                    ? "border-primary-strong bg-surface-hover shadow-[inset_0_0_0_1px_var(--color-primary-strong)]"
                    : "border-base-300 bg-base-100 hover:bg-base-200",
                  // On the children, not the label: the label carries the tooltip, and
                  // an opacity there fades the reason bubble with it.
                  option.disabled ? "cursor-not-allowed *:opacity-60" : "",
                  reason ? "tooltip" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                data-tip={reason}
              >
                <input
                  type="radio"
                  name={name}
                  className="radio radio-sm mt-0.5 shrink-0"
                  checked={selected}
                  aria-disabled={option.disabled || undefined}
                  aria-describedby={reason ? reasonId : undefined}
                  onChange={() => {
                    if (!option.disabled) {
                      onChange(option.value);
                    }
                  }}
                  data-testid={option["data-testid"]}
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    {option.icon && <Icon name={option.icon} aria-hidden />}
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="mt-1 block text-md text-muted">{option.description}</span>
                  )}
                </span>
              </label>
              {reason && (
                <span id={reasonId} className="sr-only">
                  {reason}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </fieldset>
  );
}
